import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import styles from "./TrustedContact.module.css";
import Input from "../../../Common/V2/Widgets/Input";
import Button from "../../../Common/Button/Button";
import { updateTrustedContact } from "../../../api";
import editIcon from "../../../../src/public/images/edit.svg";
import CustomSpinner from "../../../Common/Spinner";

const InfoBox = ({ className }) => (
  <div className={`${styles.infoBox} ${className || ""}`}>
    <h6>Who's a trusted contact person and why should you care?</h6>
    <p>
      A Trusted Contact Person is someone you trust (18 years or older) whom we
      may reach out to if we are unable to contact you directly or if there are
      concerns about potential fraud, financial exploitation, or account
      activity.
    </p>
    <ul>
      <li>Your Trusted Contact will not have access to your funds.</li>
      <li>
        They will only be contacted if we cannot reach you, or to confirm
        important information related to your account's safety.
      </li>
    </ul>
  </div>
);

const TrustedContact = ({ custId, userData, setUserData, featureAccess }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    address: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const isEditable = featureAccess?.modifyAccess || false;

  useEffect(() => {
    const trusted = userData?.trustedContact;
    if (trusted && trusted.firstName) {
      setShowForm(false);
      setForm({
        firstName: trusted.firstName || "",
        lastName: trusted.lastName || "",
        email: trusted.email || "",
        phoneNumber: trusted.phoneNo || "",
        address: trusted.address || "",
      });
    } else {
      setShowForm(true);
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        address: "",
      });
    }

    setInitialLoading(false);
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
    if (message) setMessage("");
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    let newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    if (!form.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required";
    if (!form.address.trim()) newErrors.address = "Address is required";

    setFieldErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber.replace(/\s+/g, ""),
        address: form.address,
      };

      const res = await updateTrustedContact(payload, custId);
      setFieldErrors({});
      setError("");

      if (res?.code === 200) {
        setMessage("Trusted contact updated successfully!");

        const updated = {
          ...userData,
          trustedContact: {
            ...payload,
            phoneNo: form.phoneNumber,
            updatedAt: new Date().toISOString(),
            createdAt:
              userData?.trustedContact?.createdAt || new Date().toISOString(),
          },
        };
        setUserData(updated);
        setShowForm(false);

        setTimeout(() => {
          setMessage("");
        }, 3000);
      } else {
        setError(res?.message || "Failed to update contact. Try again.");
      }
    } catch (err) {
      let apiError = "Something went wrong. Please try again.";
      if (err?.data) {
        if (Array.isArray(err.data.data) && err.data.data.length > 0) {
          apiError = err.data.data[0];
        } else if (err.data.message) {
          apiError = err.data.message;
        }
      } else if (err?.response?.data?.message) {
        apiError = err.response.data.message;
      }

      setError(apiError);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className={styles.loaderContainer}>
        <CustomSpinner />
      </div>
    );
  }
  if (!showForm && userData?.trustedContact) {
    const trusted = userData.trustedContact;
    return (
      <div className={styles.container}>
        <div className={styles.heading}>Trusted Contact Person</div>
        <div className={styles.mainContent}>
          <div className={styles.leftSection}>
            <InfoBox className={styles.infoBoxMobile} />

            <Row className={styles.basicDetail}>
              <Col className={styles.basicDetailText}>
                {`${trusted.firstName} ${trusted.lastName}`}
              </Col>
              <Col style={{ textAlign: "end" }}>
              {isEditable && (
                <button
                  className={styles.editButton}
                  onClick={() => {
                    setShowForm(true);
                    setFieldErrors({});
                    setError("");
                    setMessage("");
                  }}
                >
                  <img src={editIcon} className="mr-1 mb-2" alt="" /> Edit
                </button>
              )}
              </Col>
            </Row>

            <Row className={`mt-2 ${styles.borderBottom}`}>
              {[
                { label: "Email Address", value: trusted.email },
                { label: "Contact Number", value: trusted.phoneNo?.replace(/\s+/g, "") },
                { label: "Address", value: trusted.address },
              ].map((item, i) => (
                <Col key={i} md={6} xs={12} className="mb-3 mt-3">
                  <div className={styles.label}>{item.label}</div>
                  <div className={styles.value}>{item.value}</div>
                </Col>
              ))}
            </Row>
          </div>
          <InfoBox className={styles.infoBoxDesktop} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.heading}>Trusted Contact Person</div>
      <div className={styles.mainContent}>
        <div className={styles.leftSection}>
          <InfoBox className={styles.infoBoxMobile} />
          <div className={styles.mobileFormHeading}>
            {userData?.trustedContact?.firstName ? "Edit" : "Add"} Trusted
            Contact Person
          </div>
          <div className={styles.formContainer}>
            <div className={styles.formRow}>
              {["firstName", "lastName"].map((field) => (
                <div key={field} className={styles.formField}>
                  <Input
                    label={field === "firstName" ? "First Name" : "Last Name"}
                    placeholder={`Enter ${
                      field === "firstName" ? "first" : "last"
                    } name`}
                    type="text"
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    error={!!fieldErrors[field]}
                    helperText={fieldErrors[field]}
                    required
                  />
                </div>
              ))}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Input
                  label="Email"
                  placeholder="Enter email id"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  required
                />
              </div>

              <div className={styles.formField}>
                <Input
                  label="Contact Number"
                  placeholder="Enter contact number"
                  type="text"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleChange}
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber}
                  required
                />
              </div>
            </div>

            <div className={styles.addressRow}>
              <Input
                label="Address"
                isTextArea
                required
                name="address"
                placeholder="Enter address here"
                value={form.address}
                onChange={handleChange}
                error={!!fieldErrors.address}
                helperText={fieldErrors.address}
                inputClassName={styles.addressInput}
              />
            </div>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}
          {message && <div className={styles.successMessage}>{message}</div>}

         <div className={styles.buttonContainer}>
            {userData?.trustedContact?.firstName ? (
              <Button
                heading="Update Contact"
                handleClick={handleSubmit}
                loading={loading}
                disabled={loading}
                place="center"
                width="200px"
              />
            ) : featureAccess?.writeAccess ? (
              <Button
                heading="Add Contact"
                handleClick={handleSubmit}
                loading={loading}
                disabled={loading}
                place="center"
                width="200px"
              />
            ) : (
              <div className={styles.permissionMessage}>
                You don’t have permission to add a trusted contact.
              </div>
            )}
          </div>
        </div>
        <InfoBox className={styles.infoBoxDesktop} />
      </div>
    </div>
  );
};

export default TrustedContact;
